import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { LoadingController, ModalController } from '@ionic/angular';
import * as dayjs from 'dayjs';
import { GlobalConstants } from 'src/app/constant/constant';
import { TimeRecord } from 'src/app/entities/timeRecord';
import { DataChangeEmitWho } from 'src/app/models/data-change-emit/data-change-emit-payload';
import { DeviceInfoService } from 'src/app/services/device-info.service';
import { LocationService } from 'src/app/services/location.service';
import { NotificationsService } from 'src/app/services/notifications.service';
import { TimeModalHelperService } from 'src/app/services/time-modal-helper.service';
import { TimeService } from 'src/app/services/time.service';
import { TimeRecordService } from 'src/app/services/timeRecord.service';
import { DateTimeHelper } from 'src/app/utils/date-time-helper';
import { TimeFormControlParams, TimeFormDataValues } from '../time.component';
import { TOAST_CLASS, TOAST_POSITION } from 'src/app/enums/notification/notification.enum';
import { ActionValidationException, NetworkException } from 'src/app/utils/exceptions';
import { Router } from '@angular/router';
import { ClockOdometerDataValues, ClockOdometerValueComponent } from 'src/app/components/clock-odometer-value/clock-odometer-value.component';
import { LogService } from 'src/app/baseServices/logging/log.service';
import { ToastLoggingService } from 'src/app/baseServices/logging/toast-logging.service';

@Component({
  selector: 'app-clock-out',
  templateUrl: './clock-out.component.html',
  styleUrls: ['./clock-out.component.scss'],
})
export class ClockOutComponent implements OnInit {
  @Input() interval: number = GlobalConstants.DEFAULT_TIMECLOCK_INTERVAL_MINUTES;
  get INTERVAL() { return this.interval; }

  @Input() userId: number;
  @Input() selectedDate?: string;
  @Input() startDateTimeOverride?: string;
  @Input() startTimeZoneIdOverride?: string;
  @Output() formValuesChanged = new EventEmitter<TimeFormDataValues>();
  @ViewChild(ClockOdometerValueComponent) clockOdometerValueComponent: ClockOdometerValueComponent;
  timeFormControlParams: TimeFormControlParams;
  formValues: { time: TimeFormDataValues; clockOdometer: ClockOdometerDataValues; } = { time: null, clockOdometer: null };
  openTimeRecord: TimeRecord;
  isLoading: boolean = true;

  // Centralized logging context to avoid repeating the same object literal
  private get logContext() {
    return { recordId: this.openTimeRecord?.RecordId, id: this.openTimeRecord?.Id };
  }

  constructor(
    private timeService: TimeService,
    private timeRecordService: TimeRecordService,
    private locationService: LocationService,
    private deviceInfoService: DeviceInfoService,
    private loadingController: LoadingController,
    private notificationsService: NotificationsService,
    private modalController: ModalController,
    private router: Router,
    private logService: LogService,
    private toastLoggingService: ToastLoggingService,
    private timeModalHelper: TimeModalHelperService
  ) { }

  async ngOnInit() {
    await this.loadData();
    this.setFormParams();
    this.isLoading = false;
  }

  async loadData() {
    const date = this.selectedDate ?? DateTimeHelper.getLocalNowString();
    this.openTimeRecord = await this.timeRecordService.getOpenTimeRecord(this.userId, date);
  }

  setFormParams() {
    const now = DateTimeHelper.getLocalNow();
    const nowRounded = DateTimeHelper.roundDateTime(now, this.INTERVAL).tz(dayjs.tz.guess());
    const timeRecordDate = this.openTimeRecord.Date;
    const endOfDay = DateTimeHelper.getEndOfDay(timeRecordDate);
    const startOfNextDay = DateTimeHelper.getStartOfDay(DateTimeHelper.addTime(timeRecordDate, 1, 'day'));
    const startDateTime = this.startDateTimeOverride ?? this.openTimeRecord.ClockIn;
    const startTimeZoneId = this.startTimeZoneIdOverride ?? this.openTimeRecord.ClockInTimeZoneId;

    const prelimEndTimeMaxValue = dayjs.min([nowRounded, DateTimeHelper.getEndOfDay(timeRecordDate)]);
    const lastIntervalBeforeMidnight = DateTimeHelper.subtractTime(DateTimeHelper.getStartOfNextDay(timeRecordDate), this.INTERVAL, 'minute').tz(startTimeZoneId);

    this.timeFormControlParams = {
      ...this.timeFormControlParams,
      timeRecordToEditRecordId: this.openTimeRecord.RecordId,
      enclosingTimeRecordRecordId: this.openTimeRecord.RecordId,
      date: {
        initialValue: DateTimeHelper.formatDateTime(timeRecordDate),
        isDisabled: true,
      },
      startTime: {
        initialValue: DateTimeHelper.formatDateTime(startDateTime),
        isDisabled: true,
      },
      endTime: {
        initialValue: DateTimeHelper.formatDateTime(dayjs.min([nowRounded, startOfNextDay])),
        minValue: dayjs.tz(startDateTime, startTimeZoneId).tz(dayjs.tz.guess()).format('HH:mm'),
        maxValue: dayjs.min([nowRounded, lastIntervalBeforeMidnight]).format('HH:mm'),
        allowMidnight: DateTimeHelper.checkIsSame(endOfDay, 'UTC', prelimEndTimeMaxValue, 'UTC', 'minute'),
      },
      assignmentIsHidden: true,
    };
  }

  onTimeFormValuesChanged(values: TimeFormDataValues) {
    this.formValues.time = values;
  }

  onClockOdometerValuesChanged(values: ClockOdometerDataValues) {
    this.formValues.clockOdometer = values;
  }

  validateFields(): boolean {
    if (this.clockOdometerValueComponent) {
      this.clockOdometerValueComponent.markAsTouched();
    }
    return !!(this.clockOdometerValueComponent?.formGroup?.valid && this.formValues.time?.isValid);
  }

  async onSave() {
       
    if (DateTimeHelper.checkTimeIsEqual(this.formValues.time.startDateTime, this.formValues.time.startTimeZoneId, this.formValues.time.endDateTime, this.formValues.time.endTimeZoneId)) {
      await this.confirmDelete();
    } else {
      // Show odometer photo reminder before saving any records
      await this.timeModalHelper.showOdometerPhotoReminderIfNeeded(this.formValues.clockOdometer?.trackMileage, this.formValues.clockOdometer?.odometerPhoto, this.formValues.clockOdometer?.odometerValue);
      await this.processAction(this.clockOut, this.navigateToActivityLogPageAndDisplayToast);
    }
  }

  private async confirmDelete(): Promise<void> {
    this.logService.info('ClockOut.ConfirmDeletePrompt', this.logContext, true);
    const alert = await this.notificationsService.showAlert({
      header: 'Warning',
      message: 'You are clocking out at the same time as your clock in. This will undo your clock in - deleting the time record and any associated activities.',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Continue',
          handler: () => this.processAction(this.delete),
        },
      ],
    });
    await alert.present();
  }

  private async delete(): Promise<void> {
    this.logService.info('ClockOut.DeleteInitiated', this.logContext, true);
    const inputType = await this.deviceInfoService.getInputType();

    try {
      await this.timeService.deleteTime(
        DataChangeEmitWho.Self,
        this.openTimeRecord.RecordId,
        {
          InputType: inputType,
        },
        false,
        this.openTimeRecord
      );
      this.logService.info('ClockOut.DeleteSuccess', this.logContext);
    } catch (e: any) {
      this.toastLoggingService.error('ClockOut.DeleteError', e, 'Failed to delete time record.', this.logContext);
    }
  }

  private async clockOut(): Promise<void> {
    const coordinates = await this.locationService.getCurrentCoordinates();
    const localNowString = DateTimeHelper.getLocalNowString();
    const timeZoneId = DateTimeHelper.getTimeZoneId();
    const inputType = await this.deviceInfoService.getInputType();

    try {
      this.logService.info('ClockOut.Start', this.logContext);
      var clockOutResult =await this.timeService.clockOut(
        DataChangeEmitWho.Self,
        this.openTimeRecord.RecordId,
        {
          ClockOut: this.formValues.time.endDateTime,
          ClockOutTimeZoneId: this.formValues.time.endTimeZoneId,
          CapturedClockOut: localNowString,
          CapturedClockOutTimeZoneId: timeZoneId,
          Coordinates: coordinates,
          InputType: inputType,
          OdometerValue: this.formValues.clockOdometer.odometerValue,
          OdometerPhoto: this.formValues.clockOdometer.odometerPhoto,
        },
        false,
        this.openTimeRecord
      );

      this.logService.info('ClockOut.Success', this.logContext);
    } catch (e) {
      if (e instanceof ActionValidationException) {
        this.toastLoggingService.warn(
          'ClockOut.ValidationBlocked',
          'The date you are modifying has already been verified. Your supervisor will need to return your activity log to you before edits can be made.',
          this.logContext
        );
      } else {
        this.toastLoggingService.error('ClockOut.Error', e, 'An unexpected error occurred while clocking out.', this.logContext);
      }
    }
  }

  private async navigateToActivityLogPageAndDisplayToast() {
    this.router.navigate(['app/activityLog']);
    await this.notificationsService.showToast({
      message: 'You have clocked out. If you are done for the day, please sign and submit your activity log.',
      cssClass: TOAST_CLASS.SUCCESS,
      position: TOAST_POSITION.TOP,
    });
  }

  private async processAction(actionFn: () => Promise<void>, postActionFn?: () => Promise<void>): Promise<void> {
    const loading = await this.loadingController.create({ message: 'Saving time record...' });
    try {
      await loading.present();

      await actionFn.bind(this)();

      try {
        await this.timeService.syncTimeData();
      } catch (e) {
        if (e instanceof NetworkException) {
          this.toastLoggingService.warn('ClockOut.NetworkDeferred', 'Your change has been made locally. Please connect to Internet to upload this change.', this.logContext);
        }
        else {
          this.toastLoggingService.error('ClockOut.SyncError', e, 'An unexpected error occurred while syncing time data.', this.logContext);
        }
      }

      await this.onClose();

      if (postActionFn) {
        await postActionFn.bind(this)();
      }
    } finally {
      loading.dismiss();
    }
  }

  private async onClose() {
    await this.modalController.dismiss();
  }
}
